/**
 * A labelled group that expands AND retracts with one symmetric transition.
 *
 * Three things are deliberate.
 *
 * `summary` is mandatory, and it must carry the fact that makes collapsing
 * acceptable — the answer for the common case, so the group is opened by
 * exception. `7-day schedule` and a repeat of the address printed three rows
 * above are both failures: they describe the closed state instead of answering
 * from it.
 *
 * The content is ALWAYS mounted and the shutter animates
 * `grid-template-rows: 0fr → 1fr`, so retraction is the same duration and the
 * same curve as expansion. An accelerate-away exit curve on a collapsing
 * height overshoots. While collapsed the pane is `inert`, so it is out of the
 * tab order and out of the accessibility tree without being unmounted — which
 * is what lets the retraction animate at all.
 *
 * The chevron rotates on the SAME clock as the content.
 */
"use client";
import { useCallback, useId, useState, type ReactElement, type ReactNode } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  CresultcardRadiusProvider,
  useCresultcardNestedRadius,
} from "./cresultcard.radius";
import {
  CRC_ACCENT,
  CRC_DIVIDER,
  CRC_TEXT_MUTED,
  CRC_TOUCH_TARGET,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
  CRC_WELL_BACKGROUND_COLOR,
  CRC_WELL_SHADOW,
} from "./cresultcard.tokens";
import { CresultcardTextStep } from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";
import { CresultcardShutter } from "./CresultcardShutter";

const CHEVRON_SIZE = 14;
const CHEVRON_STROKE = 1.4;
const EXPAND_PREFIX = "Expand ";
const COLLAPSE_PREFIX = "Collapse ";
const TOGGLE_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Disclosure/Trigger",
  "data-crc-control": true,
};
const TOGGLE_CLASS_NAME = "cresultcard__quiet-hover";
/**
 * The Mobile summary rule.
 *
 * At 350px the label, the chevron and the toggle's own padding leave the
 * summary about 72px, and 223px of content cut into 72px turns
 * `NVDA → NVDA:NASDAQ · SEC ticker directory` into `NVDA → NVDA:N…`. A summary
 * is the fact that makes collapsing acceptable, so a summary nobody can read is
 * a disclosure with no closed state. At Mobile it takes a line of its own —
 * label and chevron above, summary across the full width below — and keeps its
 * single-line clamp there, on four times the room. Wide is untouched.
 */
const MOBILE_SUMMARY_ORDER = 1;
const MOBILE_SUMMARY_BASIS = "100%";

interface CresultcardDisclosureProps {
  readonly label: string;
  /** The fact that makes collapsing acceptable. Never a restatement. */
  readonly summary: ReactNode;
  readonly children: ReactNode;
  readonly defaultOpen?: boolean;
  readonly onToggle?: (open: boolean) => void;
}

export function CresultcardDisclosure({
  label,
  summary,
  children,
  defaultOpen = false,
  onToggle,
}: CresultcardDisclosureProps): ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  const paneId = useId();
  const radius = useCresultcardNestedRadius();
  const { isMobile } = useCresultcardViewport();

  const toggle = useCallback((): void => {
    setOpen((current) => {
      const next = !current;
      onToggle?.(next);
      return next;
    });
  }, [onToggle]);

  return (
    <div
      data-el="Cresultcard/Disclosure/Root"
      style={{
        minWidth: 0,
        overflow: "hidden",
        borderRadius: radius,
        backgroundColor: CRC_WELL_BACKGROUND_COLOR,
        boxShadow: CRC_WELL_SHADOW,
      }}
    >
      <Cbutton
        ariaLabel={`${open ? COLLAPSE_PREFIX : EXPAND_PREFIX}${label}`}
        containerStyle={{
          justifyContent: "flex-start",
          width: "100%",
          minHeight: CRC_TOUCH_TARGET,
        }}
        contentStyle={{
          display: "flex",
          alignItems: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
          gap: 10,
          width: "100%",
          minWidth: 0,
        }}
        disableClickMotion
        onClick={toggle}
        padding="9px 11px"
        preset={ButtonPreset.Ghost}
        radius={radius}
        rootAttributes={{
          ...TOGGLE_ROOT_ATTRIBUTES,
          "aria-controls": paneId,
          "aria-expanded": open,
        }}
        rootClassName={TOGGLE_CLASS_NAME}
        style={{ width: "100%" }}
      >
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
            // At Mobile the label shares its line with the chevron alone, so it
            // takes the slack and pushes the chevron to the trailing edge.
            flex: isMobile ? "1 1 auto" : "0 0 auto",
            fontSize: 8.5,
          }}
        >
          {label}
        </span>
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_TRUNCATE_STYLE,
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: isMobile ? MOBILE_SUMMARY_BASIS : "auto",
            order: isMobile ? MOBILE_SUMMARY_ORDER : undefined,
            textAlign: "start",
            color: CRC_TEXT_MUTED,
          }}
        >
          {summary}
        </span>
        <span
          aria-hidden
          className="cresultcard__chevron"
          data-crc-expanded={open}
          style={{ display: "inline-flex", color: CRC_ACCENT }}
        >
          <IconChevronDown size={CHEVRON_SIZE} stroke={CHEVRON_STROKE} />
        </span>
      </Cbutton>

      <CresultcardShutter
        id={paneId}
        open={open}
      >
        <div
          data-el="Cresultcard/Disclosure/Panel"
          style={{
            paddingTop: 2,
            paddingBottom: 11,
            paddingInline: 11,
            borderTop: `1px solid ${CRC_DIVIDER}`,
          }}
        >
          <CresultcardRadiusProvider radius={radius}>
            {children}
          </CresultcardRadiusProvider>
        </div>
      </CresultcardShutter>
    </div>
  );
}
